import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { WindowDiagram } from "@/components/WindowDiagram";
import { fmtMoney, TYPOLOGIES } from "@/lib/calc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { AuthedImage } from "@/components/AuthedImage";
import { WindowDiagram as WD2 } from "@/components/WindowDiagram";
import { toast } from "sonner";
import { Loader2, RotateCcw, Save, Zap, Droplet, Plus, Trash2, Layers, Boxes, Pencil, X, Upload, ImageIcon, ScanLine, FileUp, Check, FileText, Sparkles } from "lucide-react";

const N = (v) => Number(v) || 0;

const DIMS = {
  Fixed: [1200, 1500], Casement: [900, 1200], "Top Hung": [1200, 700],
  "Bottom Hung": [1000, 700], "Tilt & Turn": [900, 1400],
  "2 Track Sliding": [1800, 1500], "3 Track Sliding": [2400, 1800],
  "4 Track Sliding": [3200, 1800], "Corner Sliding": [2400, 1800],
  "Sliding Door": [2100, 2100], "Lift & Slide Door": [3000, 2400],
  "Slide & Fold Door": [3600, 2400], "French Door": [1500, 2400],
  "Casement Door cum Window": [2100, 2100], "Fixed + Openable Combo": [2400, 1500],
  "Ventilator Louver Type": [600, 600],
};

export default function RateLibrary() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const [baseAlu, setBaseAlu] = useState("");
  const [baseGlass, setBaseGlass] = useState("");
  const [fab, setFab] = useState("");
  const [inst, setInst] = useState("");
  const [misc, setMisc] = useState("");
  const [typos, setTypos] = useState([]);
  const [glasses, setGlasses] = useState([]);
  const [customTypos, setCustomTypos] = useState([]);
  const [customKg, setCustomKg] = useState([]);

  const apply = (d) => {
    setBaseAlu(String(d.base_alu_rate));
    setBaseGlass(String(d.base_glass_rate));
    setFab(String(d.fabrication_per_sqft));
    setInst(String(d.installation_per_sqft));
    setMisc(String(d.misc_per_sqft));
    setTypos(d.typologies.filter((t) => !t.custom).map((t) => ({ typology: t.typology, alu_kg_per_sqft: String(t.alu_kg_per_sqft) })));
    setGlasses(d.glasses.map((g) => ({
      id: g.id, name: g.name, thickness_mm: String(g.thickness_mm), other_per_sqft: String(g.other_per_sqft),
    })));
    setCustomTypos(d.custom_typologies || []);
    setCustomKg((d.custom_typologies || []).map((c) => ({
      id: c.id, typology: c.typology, alu_kg_per_sqft: String(c.alu_kg_per_sqft),
      default_glass_id: c.default_glass_id || "",
      base_typology: c.base_typology, drawing_file_id: c.drawing_file_id, drawing_filename: c.drawing_filename,
    })));
  };

  const load = async () => {
    try {
      const { data } = await api.get("/rates");
      apply(data);
    } catch (e) {
      toast.error("Failed to load rates");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const addon = N(fab) + N(inst) + N(misc);
  const aluRate = (t) => N(baseAlu) * N(t.alu_kg_per_sqft);
  const glassRate = (g) => N(g.thickness_mm) * N(baseGlass) + N(g.other_per_sqft);
  const glassRateById = (gid) => {
    const g = glasses.find((x) => x.id === gid);
    return g ? glassRate(g) : 0;
  };

  const updateTypo = (typology, value) =>
    setTypos((p) => p.map((t) => (t.typology === typology ? { ...t, alu_kg_per_sqft: value } : t)));
  const updateCustomKg = (id, value) =>
    setCustomKg((p) => p.map((c) => (c.id === id ? { ...c, alu_kg_per_sqft: value } : c)));
  const updateCustomGlass = (id, value) =>
    setCustomKg((p) => p.map((c) => (c.id === id ? { ...c, default_glass_id: value } : c)));
  const updateGlass = (i, field, value) =>
    setGlasses((p) => p.map((g, idx) => (idx === i ? { ...g, [field]: value } : g)));
  const addGlass = () =>
    setGlasses((p) => [...p, { id: null, name: "New Glass", thickness_mm: "6", other_per_sqft: "60" }]);
  const removeGlass = (i) => setGlasses((p) => p.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/rates", {
        base_alu_rate: N(baseAlu),
        base_glass_rate: N(baseGlass),
        fabrication_per_sqft: N(fab),
        installation_per_sqft: N(inst),
        misc_per_sqft: N(misc),
        typologies: typos.map((t) => ({ typology: t.typology, alu_kg_per_sqft: N(t.alu_kg_per_sqft) })),
        glasses: glasses.map((g) => ({
          id: g.id, name: g.name, thickness_mm: N(g.thickness_mm), other_per_sqft: N(g.other_per_sqft),
        })),
        custom_typologies: customKg.map((c) => ({ id: c.id, alu_kg_per_sqft: N(c.alu_kg_per_sqft), default_glass_id: c.default_glass_id || null })),
      });
      apply(data);
      toast.success("Rates saved — new items will use these prices");
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/rates/reset");
      apply(data);
      toast.success("Reset to defaults");
    } catch (e) {
      toast.error("Failed to reset");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <main className="max-w-5xl mx-auto px-6 md:px-8 py-8">
        <div className="flex items-end justify-between mb-6 gap-4">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
              Rate Library
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Rate/sqft = Aluminium + Glass + Fabrication + Installation + Misc.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" data-testid="reset-rates-button" onClick={reset} disabled={resetting} className="rounded-sm">
              {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1.5" />} Reset
            </Button>
            <Button data-testid="save-rates-button" onClick={save} disabled={saving || loading} className="rounded-sm bg-blue-600 hover:bg-blue-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save changes
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
        ) : (
          <Tabs defaultValue="composition">
            <TabsList className="rounded-sm bg-slate-100 mb-6">
              <TabsTrigger value="composition" className="rounded-sm" data-testid="tab-rate-composition">
                <Layers className="w-4 h-4 mr-1.5" /> Rate Composition
              </TabsTrigger>
              <TabsTrigger value="glass" className="rounded-sm" data-testid="tab-glass-library">
                <Droplet className="w-4 h-4 mr-1.5" /> Glass Library
              </TabsTrigger>
              <TabsTrigger value="custom" className="rounded-sm" data-testid="tab-custom-typologies">
                <Boxes className="w-4 h-4 mr-1.5" /> Custom Typologies
              </TabsTrigger>
              <TabsTrigger value="joinery" className="rounded-sm" data-testid="tab-joinery-import">
                <ScanLine className="w-4 h-4 mr-1.5" /> Joinery Import
              </TabsTrigger>
            </TabsList>

            {/* COMPOSITION */}
            <TabsContent value="composition" className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-slate-900 rounded-sm px-5 py-4 flex items-center gap-4">
                  <Zap className="w-6 h-6 text-amber-400 shrink-0" />
                  <div className="flex-1">
                    <div className="text-white font-medium text-sm">Aluminium Base Rate</div>
                    <div className="text-[11px] text-slate-400">Reprices every typology below</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-slate-400 font-mono">₹</span>
                    <Input type="number" data-testid="base-alu-input" value={baseAlu} onChange={(e) => setBaseAlu(e.target.value)}
                      className="rounded-sm h-10 w-24 font-mono text-right bg-white" />
                    <span className="text-slate-400 text-xs">/kg</span>
                  </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-sm px-5 py-4 grid grid-cols-3 gap-3">
                  <AddonField label="Fabrication" testid="fabrication-input" value={fab} onChange={setFab} />
                  <AddonField label="Installation" testid="installation-input" value={inst} onChange={setInst} />
                  <AddonField label="Misc" testid="misc-input" value={misc} onChange={setMisc} />
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="rates-table">
                <div className="hidden md:grid grid-cols-[1fr_130px_120px_140px] gap-3 px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                  <div>Typology</div>
                  <div className="text-right">Aluminium (kg/sqft)</div>
                  <div className="text-right">Alu ₹/sqft</div>
                  <div className="text-right">+ Add-ons ₹/sqft</div>
                </div>
                {typos.map((t, i) => (
                  <motion.div key={t.typology} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                    className="grid grid-cols-2 md:grid-cols-[1fr_130px_120px_140px] gap-3 px-4 py-3 items-center border-b border-slate-100 hover:bg-slate-50"
                    data-testid={`rate-row-${t.typology.replace(/ /g, "-")}`}>
                    <div className="flex items-center gap-3 col-span-2 md:col-span-1">
                      <div className="w-12 h-11 border border-slate-200 rounded-sm bg-white p-1 shrink-0">
                        <WindowDiagram typology={t.typology} width={(DIMS[t.typology] || [1200, 1200])[0]} height={(DIMS[t.typology] || [1200, 1200])[1]} className="w-full h-full" showDims={false} />
                      </div>
                      <div className="text-sm font-medium text-slate-800">{t.typology}</div>
                    </div>
                    <Input type="number" step="0.1" data-testid={`rate-kg-${t.typology.replace(/ /g, "-")}`} value={t.alu_kg_per_sqft}
                      onChange={(e) => updateTypo(t.typology, e.target.value)} className="rounded-sm h-9 font-mono text-right" />
                    <div className="text-right font-mono text-slate-700">₹ {fmtMoney(aluRate(t))}</div>
                    <div data-testid={`rate-base-${t.typology.replace(/ /g, "-")}`} className="text-right font-mono font-semibold text-slate-900 bg-blue-50 border border-blue-100 rounded-sm h-9 flex items-center justify-end px-3 text-xs">
                      ₹ {fmtMoney(aluRate(t) + addon)}
                    </div>
                  </motion.div>
                ))}
                {customKg.length > 0 && (
                  <div className="px-4 py-2 bg-blue-50/50 border-b border-blue-100 text-[10px] uppercase tracking-wider text-blue-600 font-semibold" data-testid="rate-custom-group-header">
                    Custom / Imported typologies
                  </div>
                )}
                {customKg.map((c, i) => (
                  <motion.div key={c.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                    className="grid grid-cols-2 md:grid-cols-[1fr_92px_180px_150px] gap-3 px-4 py-3 items-center border-b border-slate-100 hover:bg-slate-50"
                    data-testid={`rate-custom-row-${c.id}`}>
                    <div className="flex items-center gap-3 col-span-2 md:col-span-1">
                      <div className="w-12 h-11 border border-slate-200 rounded-sm bg-white p-1 shrink-0 overflow-hidden">
                        {c.drawing_file_id
                          ? <AuthedImage fileId={c.drawing_file_id} filename={c.drawing_filename} className="w-full h-full object-contain" />
                          : <WindowDiagram typology={c.base_typology || "Fixed"} width={1200} height={1200} className="w-full h-full" showDims={false} />}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate">{c.typology}</div>
                        {c.base_typology && <div className="text-[10px] text-slate-400 truncate">based on {c.base_typology}</div>}
                      </div>
                    </div>
                    <Input type="number" step="0.1" data-testid={`rate-custom-kg-${c.id}`} value={c.alu_kg_per_sqft}
                      onChange={(e) => updateCustomKg(c.id, e.target.value)} className="rounded-sm h-9 font-mono text-right" title="Aluminium kg/sqft" />
                    <select
                      data-testid={`rate-custom-glass-${c.id}`}
                      value={c.default_glass_id || ""}
                      onChange={(e) => updateCustomGlass(c.id, e.target.value)}
                      className="h-9 w-full rounded-sm border border-slate-200 bg-white px-2 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      title="Default glass — used to preview this typology's all-in rate">
                      <option value="">No default glass</option>
                      {glasses.map((g) => {
                        const glabel = `${g.name} — ₹${fmtMoney(glassRate(g))}`;
                        return (
                          <option key={g.id || g.name} value={g.id || ""}>{glabel}</option>
                        );
                      })}
                    </select>
                    <div className="text-right">
                      <div data-testid={`rate-custom-allin-${c.id}`} className="font-mono font-semibold text-slate-900 bg-blue-50 border border-blue-100 rounded-sm h-9 flex items-center justify-end px-3 text-xs">
                        ₹ {fmtMoney(aluRate(c) + glassRateById(c.default_glass_id) + addon)}
                      </div>
                      <div className="text-[9px] text-slate-400 mt-0.5 pr-1">all-in / sqft</div>
                    </div>
                  </motion.div>
                ))}
              </div>
              <p className="text-xs text-slate-400">
                Item rate = the ₹/sqft above (aluminium + add-ons) + the selected glass ₹/sqft.
              </p>
            </TabsContent>

            {/* GLASS */}
            <TabsContent value="glass" className="space-y-5">
              <div className="bg-slate-900 rounded-sm px-5 py-4 flex items-center gap-4">
                <Droplet className="w-6 h-6 text-sky-400 shrink-0" />
                <div className="flex-1">
                  <div className="text-white font-medium text-sm">Glass Base Rate</div>
                  <div className="text-[11px] text-slate-400">Glass ₹/sqft = thickness × base + processing</div>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-slate-400 font-mono">₹</span>
                  <Input type="number" data-testid="base-glass-input" value={baseGlass} onChange={(e) => setBaseGlass(e.target.value)}
                    className="rounded-sm h-10 w-24 font-mono text-right bg-white" />
                  <span className="text-slate-400 text-xs">/sqft·mm</span>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="glass-table">
                <div className="hidden md:grid grid-cols-[1fr_110px_130px_120px_44px] gap-3 px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                  <div>Glass Type</div>
                  <div className="text-right">Thickness (mm)</div>
                  <div className="text-right">Processing ₹/sqft</div>
                  <div className="text-right">Glass ₹/sqft</div>
                  <div></div>
                </div>
                {glasses.map((g, i) => (
                  <div key={i} data-testid={`glass-row-${i}`}
                    className="grid grid-cols-2 md:grid-cols-[1fr_110px_130px_120px_44px] gap-3 px-4 py-3 items-center border-b border-slate-100 hover:bg-slate-50">
                    <Input data-testid={`glass-name-${i}`} value={g.name} onChange={(e) => updateGlass(i, "name", e.target.value)}
                      className="rounded-sm h-9 col-span-2 md:col-span-1" />
                    <Input type="number" step="0.01" data-testid={`glass-thickness-${i}`} value={g.thickness_mm} onChange={(e) => updateGlass(i, "thickness_mm", e.target.value)}
                      className="rounded-sm h-9 font-mono text-right" />
                    <Input type="number" data-testid={`glass-other-${i}`} value={g.other_per_sqft} onChange={(e) => updateGlass(i, "other_per_sqft", e.target.value)}
                      className="rounded-sm h-9 font-mono text-right" />
                    <div data-testid={`glass-computed-${i}`} className="text-right font-mono font-semibold text-slate-900 bg-sky-50 border border-sky-100 rounded-sm h-9 flex items-center justify-end px-3 text-xs">
                      ₹ {fmtMoney(glassRate(g))}
                    </div>
                    <button data-testid={`glass-delete-${i}`} onClick={() => removeGlass(i)} className="text-slate-400 hover:text-red-600 flex justify-center">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <div className="p-3">
                  <Button variant="outline" size="sm" data-testid="add-glass-button" onClick={addGlass} className="rounded-sm">
                    <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Glass Type
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* CUSTOM TYPOLOGIES */}
            <TabsContent value="custom" className="space-y-5">
              <CustomTypologyManager
                glasses={glasses}
                items={customTypos}
                onApply={apply}
              />
            </TabsContent>

            {/* JOINERY IMPORT */}
            <TabsContent value="joinery" className="space-y-5">
              <JoineryImport glasses={glasses} onImported={apply} />
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
}

function JoineryImport({ glasses, onImported }) {
  const navigate = useNavigate();
  const [extracting, setExtracting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [quoting, setQuoting] = useState(false);
  const [result, setResult] = useState(null);
  const [rows, setRows] = useState([]);

  const extract = async (f) => {
    if (!f) return;
    setExtracting(true);
    setResult(null);
    setRows([]);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/joinery/extract", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      setResult(data);
      setRows((data.rows || []).map((r) => ({ ...r, include: true })));
      if ((data.rows || []).length) toast.success(`Found ${data.rows.length} typologies — review below`);
      else toast.warning("No typologies detected — try a clearer sheet");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI extraction failed");
    } finally {
      setExtracting(false);
    }
  };

  const upd = (i, k, v) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  const chosenRows = () =>
    rows
      .filter((r) => r.include)
      .map((r) => ({
        code: r.code, type_raw: r.type_raw, mapped_typology: r.mapped_typology,
        width_mm: Number(r.width_mm) || 0, height_mm: Number(r.height_mm) || 0,
        quantity: Number(r.quantity) || 1, glass: r.glass || "", glass_id: r.glass_id || null,
        page: r.page || 1, bbox: r.bbox || [],
      }));

  const doImport = async () => {
    const rowsPayload = chosenRows();
    if (!rowsPayload.length) { toast.error("Select at least one typology to import"); return; }
    setImporting(true);
    try {
      const body = { file_id: result.file_id, filename: result.filename, rows: rowsPayload };
      const { data } = await api.post("/joinery/import", body, { timeout: 300000 });
      onImported(data.rates);
      toast.success(`Imported ${data.imported} typologies into Rate Library & Drawing Library`);
      setResult(null);
      setRows([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const doQuotation = async () => {
    const rowsPayload = chosenRows();
    if (!rowsPayload.length) { toast.error("Select at least one unit for the quotation"); return; }
    setQuoting(true);
    try {
      const body = { file_id: result.file_id, filename: result.filename, rows: rowsPayload };
      const { data } = await api.post("/joinery/quotation", body, { timeout: 300000 });
      toast.success(`Draft quotation created with ${data.imported} units`);
      navigate(`/quotation/${data.quote_id}/edit`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create the quotation");
    } finally {
      setQuoting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="joinery-import-panel">
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <div className="flex items-center gap-2 mb-1">
          <ScanLine className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-slate-800 text-sm">Import typologies from a joinery drawing</h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Upload an architect's window/door schedule (PDF or image). AI reads every unit — code, type,
          dimensions, quantity and glass — for you to review and edit. On import, each unit's elevation is
          automatically redrawn in the app's slate/glass style with dimension lines removed.
        </p>
        <label
          data-testid="joinery-file-input"
          className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-sm py-10 cursor-pointer hover:border-blue-400 hover:bg-blue-50/40 transition-colors"
        >
          {extracting ? (
            <>
              <Loader2 className="w-7 h-7 text-blue-600 animate-spin" />
              <span className="text-sm text-slate-600">Reading the joinery sheet with AI…</span>
              <span className="text-[11px] text-slate-400">This can take up to a minute for large sheets</span>
            </>
          ) : (
            <>
              <FileUp className="w-7 h-7 text-slate-400" />
              <span className="text-sm text-slate-600 font-medium">Click to upload a joinery PDF / image</span>
              <span className="text-[11px] text-slate-400">PDF, PNG or JPG</span>
            </>
          )}
          <input
            type="file"
            accept="application/pdf,image/*"
            className="hidden"
            disabled={extracting}
            onChange={(e) => extract(e.target.files?.[0])}
          />
        </label>
      </div>

      {rows.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="joinery-review">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
            <div className="text-sm font-semibold text-slate-700">
              Review &amp; edit — {rows.filter((r) => r.include).length} of {rows.length} selected
            </div>
            <div className="flex items-center gap-2">
              <Button data-testid="joinery-import-btn" onClick={doImport} disabled={importing || quoting} variant="outline" className="rounded-sm border-slate-300">
                {importing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Check className="w-4 h-4 mr-1.5" />}
                Import to library
              </Button>
              <Button data-testid="joinery-quotation-btn" onClick={doQuotation} disabled={importing || quoting} className="rounded-sm bg-blue-600 hover:bg-blue-700">
                {quoting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <FileText className="w-4 h-4 mr-1.5" />}
                Create draft quotation
              </Button>
            </div>
          </div>
          <div className="px-4 py-2 text-[11px] text-slate-500 border-b border-slate-100 bg-blue-50/40">
            <b>Create draft quotation</b> builds a full quotation with every selected unit pre-filled — quantity, dimensions,
            glass, auto rate, computed sq.ft area and the unit's own elevation drawing from the sheet. <b>Import to library</b> only
            adds them as reusable custom typologies.
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="px-2 py-2 w-8"></th>
                  <th className="px-2 py-2 text-left">Code</th>
                  <th className="px-2 py-2 text-left">Type (mapped)</th>
                  <th className="px-2 py-2 text-right">W (mm)</th>
                  <th className="px-2 py-2 text-right">H (mm)</th>
                  <th className="px-2 py-2 text-right">Qty</th>
                  <th className="px-2 py-2 text-left">Glass</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} data-testid={`joinery-row-${i}`} className={`border-b border-slate-100 ${r.include ? "" : "opacity-40"}`}>
                    <td className="px-2 py-1.5 text-center">
                      <input type="checkbox" data-testid={`joinery-include-${i}`} checked={r.include}
                        onChange={(e) => upd(i, "include", e.target.checked)} />
                    </td>
                    <td className="px-2 py-1.5">
                      <Input data-testid={`joinery-code-${i}`} value={r.code} onChange={(e) => upd(i, "code", e.target.value)} className="h-8 rounded-sm w-20" />
                    </td>
                    <td className="px-2 py-1.5">
                      <select data-testid={`joinery-type-${i}`} value={r.mapped_typology}
                        onChange={(e) => upd(i, "mapped_typology", e.target.value)}
                        className="h-8 rounded-sm border border-slate-200 bg-white px-1 text-xs w-44">
                        {TYPOLOGIES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                      {r.type_raw && r.type_raw.toLowerCase() !== r.mapped_typology.toLowerCase() && (
                        <div className="text-[9px] text-slate-400 mt-0.5">sheet: “{r.type_raw}”</div>
                      )}
                    </td>
                    <td className="px-2 py-1.5">
                      <Input type="number" data-testid={`joinery-w-${i}`} value={r.width_mm} onChange={(e) => upd(i, "width_mm", e.target.value)} className="h-8 rounded-sm w-20 font-mono text-right" />
                    </td>
                    <td className="px-2 py-1.5">
                      <Input type="number" data-testid={`joinery-h-${i}`} value={r.height_mm} onChange={(e) => upd(i, "height_mm", e.target.value)} className="h-8 rounded-sm w-20 font-mono text-right" />
                    </td>
                    <td className="px-2 py-1.5">
                      <Input type="number" data-testid={`joinery-qty-${i}`} value={r.quantity} onChange={(e) => upd(i, "quantity", e.target.value)} className="h-8 rounded-sm w-14 font-mono text-right" />
                    </td>
                    <td className="px-2 py-1.5">
                      <select data-testid={`joinery-glass-${i}`} value={r.glass_id || ""}
                        onChange={(e) => upd(i, "glass_id", e.target.value || null)}
                        className="h-8 rounded-sm border border-slate-200 bg-white px-1 text-xs w-40">
                        <option value="">{r.glass ? `(${r.glass})` : "— none —"}</option>
                        {glasses.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function AddonField({ label, testid, value, onChange }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-wider text-slate-400">{label} ₹/sqft</Label>
      <Input type="number" data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="rounded-sm h-9 font-mono text-right" />
    </div>
  );
}

function CustomTypologyManager({ glasses, items, onApply }) {
  const empty = {
    name: "", weight_kg: "1.5", base_typology: "Fixed", default_glass_id: glasses[0]?.id || "",
    frame_depth: "", glazing: "", hardware: "", default_width_mm: "", default_height_mm: "",
    drawing_file_id: null, drawing_filename: "",
  };
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const cancel = () => { setEditingId(null); setForm({ ...empty, default_glass_id: glasses[0]?.id || "" }); };
  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({
      name: c.typology, weight_kg: String(c.weight_kg ?? c.alu_kg_per_sqft ?? 1.5),
      base_typology: c.base_typology || "Fixed",
      default_glass_id: c.default_glass_id || glasses[0]?.id || "",
      frame_depth: c.frame_depth || "", glazing: c.glazing || "", hardware: c.hardware || "",
      default_width_mm: c.default_width_mm ? String(c.default_width_mm) : "",
      default_height_mm: c.default_height_mm ? String(c.default_height_mm) : "",
      drawing_file_id: c.drawing_file_id || null, drawing_filename: c.drawing_filename || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, drawing_file_id: data.id, drawing_filename: data.filename }));
      toast.success("Drawing uploaded");
    } catch { toast.error("Drawing upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Typology name is required"); return; }
    setBusy(true);
    try {
      const body = {
        name: form.name.trim(), weight_kg: N(form.weight_kg),
        base_typology: form.base_typology || "Fixed",
        default_glass_id: form.default_glass_id || null,
        frame_depth: form.frame_depth, glazing: form.glazing, hardware: form.hardware,
        default_width_mm: N(form.default_width_mm), default_height_mm: N(form.default_height_mm),
        drawing_file_id: form.drawing_file_id, drawing_filename: form.drawing_filename,
      };
      const { data } = editingId
        ? await api.put(`/rates/custom-typologies/${editingId}`, body)
        : await api.post("/rates/custom-typologies", body);
      onApply(data);
      toast.success(editingId ? "Typology updated" : "Custom typology added");
      cancel();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save typology"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      const { data } = await api.delete(`/rates/custom-typologies/${id}`);
      onApply(data);
      toast.success("Typology deleted");
      if (editingId === id) cancel();
    } catch { toast.error("Delete failed"); }
  };

  const [restylingAll, setRestylingAll] = useState(false);
  const [restylingId, setRestylingId] = useState(null);

  const restyleAll = async () => {
    setRestylingAll(true);
    try {
      const { data } = await api.post("/rates/custom-typologies/restyle-all", null, { timeout: 300000 });
      onApply(data.rates);
      if (data.restyled > 0) toast.success(`Restyled ${data.restyled} drawing${data.restyled > 1 ? "s" : ""} to match the app style`);
      else toast.info("All drawings are already styled");
    } catch (e) { toast.error(e.response?.data?.detail || "Restyle failed"); }
    finally { setRestylingAll(false); }
  };

  const restyleOne = async (id) => {
    setRestylingId(id);
    try {
      const { data } = await api.post(`/rates/custom-typologies/${id}/restyle`, null, { timeout: 120000 });
      onApply(data.rates);
      toast.success("Drawing restyled to match the app style");
    } catch (e) { toast.error(e.response?.data?.detail || "Restyle failed"); }
    finally { setRestylingId(null); }
  };

  const styleableCount = items.filter((c) => c.drawing_file_id && !c.restyled).length;

  return (
    <div className="space-y-5" data-testid="custom-typologies-panel">
      {/* Add / edit form */}
      <div className="bg-white border border-slate-200 rounded-sm p-5 space-y-4" data-testid="custom-typology-form">
        <div className="flex items-center gap-2">
          <Boxes className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-slate-800 text-sm">
            {editingId ? "Edit custom typology" : "Add a custom typology"}
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1 md:col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-slate-400">Name</Label>
            <Input data-testid="ct-name" value={form.name} placeholder="e.g. Special Bay Window"
              onChange={(e) => set("name", e.target.value)} className="rounded-sm h-9" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-slate-400">Aluminium (kg/sqft)</Label>
            <Input type="number" step="0.1" data-testid="ct-weight" value={form.weight_kg}
              onChange={(e) => set("weight_kg", e.target.value)} className="rounded-sm h-9 font-mono text-right" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-slate-400">Diagram base shape</Label>
            <select data-testid="ct-base" value={form.base_typology}
              onChange={(e) => set("base_typology", e.target.value)}
              className="w-full h-9 rounded-sm border border-slate-200 bg-white px-2 text-sm">
              {TYPOLOGIES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-slate-400">Default glass</Label>
            <select data-testid="ct-glass" value={form.default_glass_id}
              onChange={(e) => set("default_glass_id", e.target.value)}
              className="w-full h-9 rounded-sm border border-slate-200 bg-white px-2 text-sm">
              <option value="">— none —</option>
              {glasses.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-slate-400">Default width (mm)</Label>
            <Input type="number" data-testid="ct-width" value={form.default_width_mm} placeholder="optional"
              onChange={(e) => set("default_width_mm", e.target.value)} className="rounded-sm h-9 font-mono text-right" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-slate-400">Default height (mm)</Label>
            <Input type="number" data-testid="ct-height" value={form.default_height_mm} placeholder="optional"
              onChange={(e) => set("default_height_mm", e.target.value)} className="rounded-sm h-9 font-mono text-right" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-slate-400">Frame depth</Label>
            <Input data-testid="ct-frame" value={form.frame_depth} placeholder="e.g. 90 mm"
              onChange={(e) => set("frame_depth", e.target.value)} className="rounded-sm h-9" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-slate-400">Glazing</Label>
            <Input data-testid="ct-glazing" value={form.glazing} placeholder="e.g. DGU 24mm"
              onChange={(e) => set("glazing", e.target.value)} className="rounded-sm h-9" />
          </div>
          <div className="space-y-1 md:col-span-3">
            <Label className="text-[10px] uppercase tracking-wider text-slate-400">Hardware / spec notes</Label>
            <Textarea data-testid="ct-hardware" value={form.hardware} rows={2}
              placeholder="e.g. YKK AP multipoint lock · SS friction stays · EPDM seals"
              onChange={(e) => set("hardware", e.target.value)} className="rounded-sm text-sm" />
          </div>
        </div>

        {/* Drawing upload */}
        <div className="flex items-center gap-3">
          <label data-testid="ct-drawing-upload" className="inline-flex items-center gap-2 cursor-pointer text-sm text-blue-600 hover:text-blue-700 border border-blue-200 rounded-sm px-3 py-1.5">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {form.drawing_file_id ? "Replace drawing" : "Upload drawing"}
            <input type="file" accept="image/*,application/pdf" className="hidden"
              onChange={(e) => upload(e.target.files?.[0])} />
          </label>
          {form.drawing_file_id && (
            <div className="flex items-center gap-2">
              <div className="w-16 h-12 border border-slate-200 rounded-sm overflow-hidden bg-slate-50">
                <AuthedImage fileId={form.drawing_file_id} filename={form.drawing_filename} className="w-full h-full object-contain" />
              </div>
              <button data-testid="ct-drawing-remove" onClick={() => set("drawing_file_id", null)}
                className="text-slate-400 hover:text-red-600"><X className="w-4 h-4" /></button>
            </div>
          )}
        </div>

        <div className="flex gap-2 pt-1">
          <Button data-testid="ct-save" onClick={submit} disabled={busy} className="rounded-sm bg-blue-600 hover:bg-blue-700">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingId ? <Save className="w-4 h-4 mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />)}
            {editingId ? "Update typology" : "Add typology"}
          </Button>
          {editingId && (
            <Button variant="outline" data-testid="ct-cancel" onClick={cancel} className="rounded-sm">Cancel</Button>
          )}
        </div>
      </div>

      {/* Existing list */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="custom-typologies-table">
        {items.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
            <div className="text-sm font-semibold text-slate-700">
              Custom typologies — {items.length}
              {styleableCount > 0 && (
                <span className="ml-2 text-[11px] font-normal text-slate-500">{styleableCount} drawing{styleableCount > 1 ? "s" : ""} not yet restyled</span>
              )}
            </div>
            <Button
              data-testid="restyle-all-btn"
              onClick={restyleAll}
              disabled={restylingAll || items.every((c) => !c.drawing_file_id)}
              variant="outline"
              className="rounded-sm border-blue-200 text-blue-700 hover:bg-blue-50"
              title="Use AI to redraw every uploaded drawing in the app's slate/glass elevation style and remove dimensions"
            >
              {restylingAll ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
              Restyle drawings (AI)
            </Button>
          </div>
        )}
        {items.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-slate-400">
            No custom typologies yet. Add one above — it'll appear in the Quotation Builder dropdown.
          </div>
        ) : items.map((c) => (
          <div key={c.id} data-testid={`custom-typo-row-${c.id}`}
            className="grid grid-cols-[56px_1fr_120px_112px] gap-3 px-4 py-3 items-center border-b border-slate-100 hover:bg-slate-50">
            <div className="relative w-14 h-12 border border-slate-200 rounded-sm bg-white p-1 overflow-hidden">
              {c.drawing_file_id
                ? <AuthedImage fileId={c.drawing_file_id} filename={c.drawing_filename} className="w-full h-full object-contain" />
                : <WD2 typology={c.base_typology || c.typology} width={1200} height={1200} className="w-full h-full" showDims={false} />}
              {c.restyled && (
                <span data-testid={`ct-styled-${c.id}`} className="absolute -top-1 -right-1 bg-blue-600 text-white rounded-full p-0.5" title="Restyled to app style">
                  <Sparkles className="w-2.5 h-2.5" />
                </span>
              )}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-800 truncate">{c.typology}</div>
              <div className="text-[11px] text-slate-500 truncate">
                {c.base_typology ? `${c.base_typology} · ` : ""}{c.weight_kg} kg/sqft{c.default_width_mm ? ` · ${c.default_width_mm}×${c.default_height_mm}mm` : ""}{c.frame_depth ? ` · ${c.frame_depth}` : ""}
              </div>
              {c.hardware && <div className="text-[10px] text-slate-400 truncate">Hardware: {c.hardware}</div>}
            </div>
            <div className="text-right font-mono text-xs text-slate-700">
              Alu ₹ {fmtMoney(c.alu_rate)}<span className="text-slate-400">/sqft</span>
            </div>
            <div className="flex justify-end gap-1">
              {c.drawing_file_id && (
                <button data-testid={`ct-restyle-${c.id}`} onClick={() => restyleOne(c.id)} disabled={restylingId === c.id}
                  title="Restyle this drawing with AI" className="text-slate-400 hover:text-blue-600 p-1 disabled:opacity-50">
                  {restylingId === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                </button>
              )}
              <button data-testid={`ct-edit-${c.id}`} onClick={() => startEdit(c)} className="text-slate-400 hover:text-blue-600 p-1"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`ct-delete-${c.id}`} onClick={() => remove(c.id)} className="text-slate-400 hover:text-red-600 p-1"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
